/**
 * @fileoverview Footer page numbering for generated documents.
 * @module utils/branding/page-numbering
 *
 * Purpose:
 * - Firms whose letterhead numbers every page after the first can't get that
 *   from the letterhead itself: header/footer zones are cropped PNG snapshots
 *   (or legacy extracted HTML) repeated verbatim on every matching page, so
 *   nothing in them can vary by page. Page numbering is therefore its own
 *   footer element, configured on the branding profile and resolved per page
 *   at render time.
 *
 * Where the number sits:
 * - A single line at the bottom of the page, inset by the footer side padding
 *   and sitting `footerBottom` px above the page edge — i.e. below the footer
 *   letterhead when there is one. The footer band grows by
 *   {@link PAGE_NUMBER_BAND_PX} on pages that show a number so the letterhead
 *   is pushed up rather than overlapped.
 *
 * Why a fixed band height instead of a measured one:
 * - The line is a single run of text at a size we control, so its height is
 *   deterministic. Measuring it would mean threading another value through
 *   the measure/paginate pipeline for no accuracy gain, and the number only
 *   appears on *some* pages so it can't simply be folded into the measured
 *   footer height.
 *
 * Used by:
 * - `src/utils/export/exportHtmlDocument.ts` — browser PDF + Word (.docx).
 * - `src/components/templates/PaginatedLetterPreview.tsx` — on-screen preview.
 * - `src/routes/_app/_auth/dashboard/_layout.settings.branding.tsx` — settings.
 */

import { shouldRuleShowOnPage, type PageRule } from "./resolve-letterhead";

/** How the number reads on the page. */
export type PageNumberFormat = "bare" | "page_x" | "page_x_of_y";

/** Horizontal placement within the footer band. */
export type PageNumberAlignment = "left" | "center" | "right";

export interface PageNumberingConfig {
  /** Off by default — existing firms see no change until they opt in. */
  enabled: boolean;
  format: PageNumberFormat;
  alignment: PageNumberAlignment;
  /**
   * Which pages carry a number. Independent of the footer letterhead's own
   * rule: a firm may run its footer artwork on every page but number only
   * from page 2 (`notFirst`), or have no footer artwork at all.
   */
  pageRule: PageRule;
}

/**
 * Applied when a firm enables numbering without changing anything else.
 * `notFirst` because a covering letter's first page conventionally carries
 * no number, which is also the case that prompted this feature.
 */
export const DEFAULT_PAGE_NUMBERING: Readonly<PageNumberingConfig> = {
  enabled: false,
  format: "page_x_of_y",
  alignment: "center",
  pageRule: "notFirst",
};

/** Font size of the number line, in points. */
export const PAGE_NUMBER_FONT_SIZE_PT = 9;

/** Unitless line-height of the number line. */
export const PAGE_NUMBER_LINE_HEIGHT = 1.35;

/**
 * Vertical space the number line claims inside the footer band, in CSS px.
 * One 9pt line at 1.35 (≈16px) plus a small gap so it never crowds the
 * letterhead sitting above it.
 */
export const PAGE_NUMBER_BAND_PX = 22;

/** Same allowance in TWIP, for the Word footer part. */
export const PAGE_NUMBER_BAND_TWIP = PAGE_NUMBER_BAND_PX * 15;

/**
 * A precedent template's opt-out. Templates can only suppress numbering, not
 * configure it — the wording and placement stay firm-wide so documents from
 * one firm read consistently.
 */
export interface PageNumberingTemplateOverride {
  suppressPageNumbering?: boolean | null;
}

/**
 * Read a page-numbering config off a branding profile, returning `null` when
 * numbering is off.
 *
 * Returning `null` rather than a disabled config lets every caller treat
 * "no numbering" as a single falsy check, and keeps the disabled case out of
 * the render paths entirely.
 *
 * @param source - Branding profile, or anything with a `pageNumbering` field.
 * @param templateOverride - The precedent template the document came from.
 *   Its `suppressPageNumbering` flag turns numbering off for this document
 *   even when the firm has it on.
 * @returns The config, or `null` when absent, disabled, set to "never", or
 *   suppressed by the template.
 *
 * @example
 * const numbering = resolvePageNumbering(brandingProfile, template);
 * if (numbering) reserveFooterSpace();
 */
export function resolvePageNumbering(
  source?: { pageNumbering?: Partial<PageNumberingConfig> | null } | null,
  templateOverride?: PageNumberingTemplateOverride | null,
): PageNumberingConfig | null {
  if (templateOverride?.suppressPageNumbering) return null;
  const raw = source?.pageNumbering;
  if (!raw?.enabled) return null;
  const pageRule = raw.pageRule ?? DEFAULT_PAGE_NUMBERING.pageRule;
  if (pageRule === "none") return null;
  return {
    enabled: true,
    format: raw.format ?? DEFAULT_PAGE_NUMBERING.format,
    alignment: raw.alignment ?? DEFAULT_PAGE_NUMBERING.alignment,
    pageRule,
  };
}

/**
 * Does this page carry a number?
 *
 * @param config    Resolved config, or `null` when numbering is off.
 * @param pageIndex 1-based page index.
 */
export function shouldShowPageNumber(
  config: PageNumberingConfig | null | undefined,
  pageIndex: number,
): boolean {
  if (!config) return false;
  return shouldRuleShowOnPage(config.pageRule, pageIndex);
}

/**
 * Render the number as text.
 *
 * @param format     Chosen format.
 * @param pageIndex  1-based page index.
 * @param totalPages Total pages in the document.
 *
 * @example
 * formatPageNumber("page_x_of_y", 2, 5); // "Page 2 of 5"
 * formatPageNumber("bare", 2, 5);        // "2"
 */
export function formatPageNumber(
  format: PageNumberFormat,
  pageIndex: number,
  totalPages: number,
): string {
  switch (format) {
    case "bare":
      return `${pageIndex}`;
    case "page_x":
      return `Page ${pageIndex}`;
    case "page_x_of_y":
      return `Page ${pageIndex} of ${totalPages}`;
    default: {
      const exhaustive: never = format;
      return exhaustive;
    }
  }
}

/**
 * Footer bottom padding to use for the letterhead content, given whether
 * this page also shows a number. The extra allowance keeps the letterhead
 * clear of the number line, which is anchored below it.
 *
 * @param footerBottomPx Configured footer bottom padding.
 * @param showsNumber    Whether this page shows a page number.
 */
export function footerBottomPaddingWithPageNumber(
  footerBottomPx: number,
  showsNumber: boolean,
): number {
  return showsNumber ? footerBottomPx + PAGE_NUMBER_BAND_PX : footerBottomPx;
}

/** Shared CSS declarations for the number line (browser surfaces). */
function pageNumberTextCss(
  alignment: PageNumberAlignment,
  fontFamily?: string | null,
  colour?: string | null,
): string {
  const declarations = [
    `text-align:${alignment}`,
    `font-size:${PAGE_NUMBER_FONT_SIZE_PT}pt`,
    `line-height:${PAGE_NUMBER_LINE_HEIGHT}`,
  ];
  // Firm-admin config, but these land inside a style="" attribute — strip
  // anything that could terminate the attribute or inject declarations.
  // Also strip `&`: HTML attribute parsing decodes numeric character
  // references even without a trailing semicolon (e.g. `&#34` -> `"`), so an
  // ampersand can smuggle back any character the other strips removed.
  const cssValue = (v: string) => v.replace(/["'<>;{}&\\]/g, "").trim();
  const safeFont = fontFamily ? cssValue(fontFamily) : "";
  const safeColour = colour ? cssValue(colour) : "";
  if (safeFont) declarations.push(`font-family:${safeFont}`);
  if (safeColour) declarations.push(`color:${safeColour}`);
  return declarations.join(";");
}

/**
 * Build the absolutely-positioned page-number line for an HTML page.
 *
 * Positioned against the enclosing `.print-page`, so the caller passes
 * insets that already account for any side letterhead bands.
 *
 * @returns The line's HTML, or `""` when this page carries no number.
 *
 * @example
 * buildPageNumberHtml({
 *   config, pageIndex: 2, totalPages: 5,
 *   leftPx: 56, rightPx: 56, bottomPx: 24,
 * });
 */
export function buildPageNumberHtml(opts: {
  config: PageNumberingConfig | null | undefined;
  pageIndex: number;
  totalPages: number;
  leftPx: number;
  rightPx: number;
  bottomPx: number;
  fontFamily?: string | null;
  colour?: string | null;
}): string {
  const { config, pageIndex, totalPages } = opts;
  if (!config || !shouldShowPageNumber(config, pageIndex)) return "";
  const position = `position:absolute;left:${opts.leftPx}px;right:${opts.rightPx}px;bottom:${opts.bottomPx}px`;
  const text = pageNumberTextCss(
    config.alignment,
    opts.fontFamily,
    opts.colour,
  );
  const label = formatPageNumber(config.format, pageIndex, totalPages);
  return `<div class="print-page-number" style="${position};${text}">${label}</div>`;
}

/* ============================================================
   WORD (.docx) FOOTER PARAGRAPH
   ============================================================ */

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * First family from a CSS font stack, unquoted — `w:rFonts` takes a single
 * typeface name, not a fallback list.
 */
function primaryFontName(fontFamily?: string | null): string | null {
  const first = fontFamily
    ?.split(",")[0]
    ?.trim()
    .replace(/^["']|["']$/g, "");
  return first ? first : null;
}

/** Six-digit hex (no `#`) for `w:color`, or `null` when unusable. */
function wordHexColour(colour?: string | null): string | null {
  const match = colour?.trim().match(/^#?([0-9a-f]{6})$/i);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Build the page-number paragraph for a Word footer part.
 *
 * Word computes the number itself via `PAGE` / `NUMPAGES` fields, so unlike
 * the HTML surfaces there is no per-page rendering here: one paragraph is
 * emitted into the footer part for whichever page classes carry a number.
 * The `separate`/`end` run pair wraps a placeholder that Word replaces on
 * open (and on print), which is also what keeps the number correct when the
 * user edits the document and the page count changes.
 *
 * @param opts.spacingBeforeTwip Gap above the line — used to push it clear of
 *   a footer letterhead image sitting in the same part.
 *
 * @example
 * buildWordPageNumberParagraphXml({ config, spacingBeforeTwip: 120 });
 */
export function buildWordPageNumberParagraphXml(opts: {
  config: PageNumberingConfig;
  spacingBeforeTwip?: number;
  fontFamily?: string | null;
  colour?: string | null;
}): string {
  const { config } = opts;
  const fontName = primaryFontName(opts.fontFamily);
  const hex = wordHexColour(opts.colour);
  const rPr =
    `<w:rPr>` +
    (fontName
      ? `<w:rFonts w:ascii="${escapeXml(fontName)}" w:hAnsi="${escapeXml(fontName)}"/>`
      : "") +
    // w:sz is in half-points.
    `<w:sz w:val="${PAGE_NUMBER_FONT_SIZE_PT * 2}"/>` +
    (hex ? `<w:color w:val="${hex}"/>` : "") +
    `</w:rPr>`;

  const text = (value: string) =>
    `<w:r>${rPr}<w:t xml:space="preserve">${escapeXml(value)}</w:t></w:r>`;
  const field = (instruction: string) =>
    `<w:r>${rPr}<w:fldChar w:fldCharType="begin"/></w:r>` +
    `<w:r>${rPr}<w:instrText xml:space="preserve"> ${instruction} </w:instrText></w:r>` +
    `<w:r>${rPr}<w:fldChar w:fldCharType="separate"/></w:r>` +
    `<w:r>${rPr}<w:t>1</w:t></w:r>` +
    `<w:r>${rPr}<w:fldChar w:fldCharType="end"/></w:r>`;

  const runs: string[] = [];
  if (config.format !== "bare") runs.push(text("Page "));
  runs.push(field("PAGE"));
  if (config.format === "page_x_of_y") {
    runs.push(text(" of "));
    runs.push(field("NUMPAGES"));
  }

  const before = opts.spacingBeforeTwip ?? 0;
  const spacing = `<w:spacing ${before > 0 ? `w:before="${before}" ` : ""}w:after="0" w:lineRule="auto"/>`;
  return `<w:p><w:pPr><w:jc w:val="${config.alignment}"/>${spacing}</w:pPr>${runs.join("")}</w:p>`;
}
